import { useMemo, useState } from 'react';

import {
  useChannels,
  useDeactivateOperator,
  useInviteOperator,
  useOperators,
  useUpdateOperator,
  type OperatorRow,
} from '../../api/queries.js';
import { useAuthStore } from '../../auth/auth-store.js';
import {
  Button,
  channelLabel,
  type ChannelType,
  type Column,
  ConfirmModal,
  DataTable,
  DrawerPanel,
  Field,
  FilterChip,
  IconButton,
  Input,
  OperatorAvatar,
  PageHeader,
  RoleBadge,
  Select,
  Textarea,
  timeAgo,
} from '../../components/ui/index.js';

const ROLE_RANK: Record<string, number> = { owner: 4, admin: 3, supervisor: 2, operator: 1 };
/** Источники, доступные для назначения (типы каналов). */
const SOURCE_TYPES: ChannelType[] = [
  'telegram',
  'whatsapp',
  'vk',
  'email',
  'sms',
  'chat',
  'wildberries',
  'ozon',
  'avito',
];

export function SettingsTeam(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const opsQ = useOperators(slug);
  const channelsQ = useChannels(slug);
  const invite = useInviteOperator(slug);
  const updateOp = useUpdateOperator(slug);
  const deactivateOp = useDeactivateOperator(slug);
  const connectedSources = new Set((channelsQ.data ?? []).map((c) => c.type));

  const [filter, setFilter] = useState<'all' | 'online' | 'offline'>('all');
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState('operator');

  const [editOp, setEditOp] = useState<OperatorRow | null>(null);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('operator');
  const [editSources, setEditSources] = useState<string[]>([]);
  const [deactivateOp_, setDeactivateOp] = useState<OperatorRow | null>(null);

  // Ранг текущего пользователя — править можно только строго ниже себя.
  const myRank = ROLE_RANK[user?.role ?? 'operator'] ?? 0;
  const canManage = editOp ? myRank > (ROLE_RANK[editOp.role] ?? 0) : false;

  const filtered = useMemo(() => {
    let r = opsQ.data ?? [];
    if (filter === 'online') r = r.filter((o) => o.status === 'online' || o.status === 'busy');
    if (filter === 'offline') r = r.filter((o) => o.status === 'offline' || o.status === 'away');
    if (roleFilter) r = r.filter((o) => o.role === roleFilter);
    return r;
  }, [opsQ.data, filter, roleFilter]);

  function openEdit(op: OperatorRow): void {
    setEditOp(op);
    setEditName(op.name);
    setEditRole(op.role);
    setEditSources(op.allowed_sources ?? []);
  }

  function toggleSource(t: string): void {
    setEditSources((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  }

  async function handleEditSave(): Promise<void> {
    if (!editOp) return;
    await updateOp.mutateAsync({
      id: editOp.id,
      patch: { name: editName, role: editRole, allowed_sources: editSources },
    });
    setEditOp(null);
  }

  const columns: Column<OperatorRow>[] = [
    {
      key: 'op',
      label: 'Оператор',
      render: (r) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <OperatorAvatar
            user={r}
            size="sm"
            status={r.status as 'online' | 'busy' | 'away' | 'offline'}
          />
          <span>
            <div style={{ fontWeight: 500 }}>{r.name}</div>
            <div style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
              {r.email}
            </div>
          </span>
        </span>
      ),
    },
    { key: 'role', label: 'Роль', width: 140, render: (r) => <RoleBadge role={r.role} /> },
    {
      key: 'status',
      label: 'Статус',
      width: 120,
      render: (r) => (
        <span
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--inbx-fz-sm)' }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background:
                r.status === 'online'
                  ? 'var(--inbx-success)'
                  : r.status === 'busy'
                    ? 'var(--inbx-warning)'
                    : 'var(--inbx-text-muted)',
            }}
          />
          {r.status}
        </span>
      ),
    },
    {
      key: '2fa',
      label: '2FA',
      width: 70,
      render: (r) =>
        (r as { two_fa_enabled?: boolean }).two_fa_enabled ? (
          <span style={{ color: 'var(--inbx-success)' }}>✓</span>
        ) : (
          <span style={{ color: 'var(--inbx-text-tertiary)' }}>—</span>
        ),
    },
    {
      key: 'last',
      label: 'Последняя активность',
      width: 160,
      render: (r) => {
        const ts = (r as { last_login_at?: string | null }).last_login_at;
        return (
          <span style={{ color: 'var(--inbx-text-tertiary)' }}>
            {ts ? timeAgo(new Date(ts)) : '—'}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: 100,
      render: (r) => (
        <span style={{ display: 'flex', gap: 2 }}>
          <IconButton icon="ti-edit" tooltip="Редактировать" onClick={() => openEdit(r)} />
          <IconButton
            icon="ti-user-off"
            tooltip="Деактивировать"
            onClick={() => setDeactivateOp(r)}
          />
        </span>
      ),
    },
  ];

  async function onInvite(): Promise<void> {
    const list = emails
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    for (const email of list) {
      try {
        await invite.mutateAsync({ email, role });
      } catch (_e) {
        /* skip failed invites */
      }
    }
    setEmails('');
    setInviteOpen(false);
  }

  return (
    <div className="inbx-page">
      <PageHeader
        title="Команда"
        badge={{ count: opsQ.data?.length ?? 0 }}
        actions={
          <Button variant="primary" icon="ti-user-plus" onClick={() => setInviteOpen(true)}>
            Пригласить
          </Button>
        }
        filters={
          <>
            <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
              Все
            </FilterChip>
            <FilterChip active={filter === 'online'} onClick={() => setFilter('online')}>
              Онлайн
            </FilterChip>
            <FilterChip active={filter === 'offline'} onClick={() => setFilter('offline')}>
              Оффлайн
            </FilterChip>
            {['owner', 'admin', 'supervisor', 'operator'].map((r) => (
              <FilterChip
                key={r}
                active={roleFilter === r}
                onClick={() => setRoleFilter(roleFilter === r ? null : r)}
              >
                <RoleBadge role={r} />
              </FilterChip>
            ))}
          </>
        }
      />
      <div className="inbx-page__body" style={{ padding: 0 }}>
        <DataTable rows={filtered} columns={columns} loading={opsQ.isLoading} />
      </div>

      <DrawerPanel
        open={inviteOpen}
        title="Пригласить операторов"
        onClose={() => setInviteOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setInviteOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={onInvite}
              disabled={!emails.trim() || invite.isPending}
            >
              {invite.isPending ? 'Отправка…' : 'Отправить приглашения'}
            </Button>
          </>
        }
      >
        <Field label="Email (несколько через запятую или с новой строки)" required>
          <Textarea
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder={'alice@team.com, bob@team.com\ncharlie@team.com'}
            rows={4}
          />
        </Field>
        <Field label="Роль">
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="operator">Operator</option>
            <option value="supervisor">Supervisor</option>
            <option value="admin">Admin</option>
          </Select>
        </Field>
        <Field label="Персональное сообщение">
          <Textarea placeholder="Привет! Присоединяйся к нашей команде в INBX..." />
        </Field>
      </DrawerPanel>

      <DrawerPanel
        open={!!editOp}
        title={`Редактировать: ${editOp?.name ?? ''}`}
        onClose={() => setEditOp(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditOp(null)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleEditSave}
              disabled={updateOp.isPending || !canManage}
            >
              {updateOp.isPending ? 'Сохранение…' : 'Сохранить'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!canManage && (
            <div
              style={{
                fontSize: 'var(--inbx-fz-sm)',
                color: 'var(--inbx-danger, #dc2626)',
                background: 'var(--inbx-bg-secondary)',
                padding: 10,
                borderRadius: 'var(--inbx-radius)',
              }}
            >
              Изменять можно только пользователей со статусом ниже вашего.
            </div>
          )}
          <Field label="Имя">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              disabled={!canManage}
            />
          </Field>
          <Field label="Роль">
            <Select
              value={editRole}
              onChange={(e) => setEditRole(e.target.value)}
              disabled={!canManage}
            >
              <option value="operator">Operator</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </Select>
          </Field>
          <Field label="Источники для приёма">
            <div style={{ fontSize: 'var(--inbx-fz-sm)', color: 'var(--inbx-text-tertiary)', marginBottom: 6 }}>
              Откуда этот оператор принимает обращения. Ничего не отмечено = принимает из всех
              источников.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {SOURCE_TYPES.map((t) => (
                <label
                  key={t}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: canManage ? 'pointer' : 'default' }}
                >
                  <input
                    type="checkbox"
                    checked={editSources.includes(t)}
                    disabled={!canManage}
                    onChange={() => toggleSource(t)}
                  />
                  {channelLabel(t)}
                  {!connectedSources.has(t) && (
                    <span style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
                      · не подключён
                    </span>
                  )}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Email">
            <Input value={editOp?.email ?? ''} disabled />
          </Field>
        </div>
      </DrawerPanel>

      <ConfirmModal
        open={!!deactivateOp_}
        title="Деактивировать оператора?"
        description={`Оператор «${deactivateOp_?.name ?? ''}» потеряет доступ к workspace. Это действие можно отменить через повторное приглашение.`}
        confirmLabel="Деактивировать"
        variant="danger"
        onConfirm={() => {
          if (deactivateOp_) deactivateOp.mutate(deactivateOp_.id);
          setDeactivateOp(null);
        }}
        onCancel={() => setDeactivateOp(null)}
      />
    </div>
  );
}
